import { useInfiniteQuery, useQuery } from '@tanstack/react-query';
import { questionApi } from '../api/question.api';
import { adApi } from '../api/ad.api';
import { userRewardApi } from '../api/reward.api';
import { weightedShuffle } from '../util/weightedRandom';
import { useEffect, useMemo, useRef, useState } from 'react';
import CategoryFilter from '../components/CategoryFilter';
import { GridSkeleton, SectionLoading } from '../components/ui/skeleton';
import QuestionCardV2 from '../components/QuestionCardV2';
import AdCard from '../components/ad/AdCard';
import { useNavigate } from 'react-router-dom';
import { QuestionV2 } from '../types/Question.type';
import { ISlice } from '../types/Page.type';
import type { LeaderboardRankingResponse, LeaderboardTierItem } from '../types/Reward.api.type';

/** S6-27: 순위 메달 */
const RANK_MEDALS: Record<number, string> = { 1: '🥇', 2: '🥈', 3: '🥉' };

export function PopularQuestionPage() {
  /**
   * States
   */
  const [selectedCategory, setSelectedCategory] = useState<string>('all');
  const [activeTab, setActiveTab] = useState<'popular' | 'ranking'>('popular');
  const [rankingType, setRankingType] = useState<'vote' | 'like'>('vote'); // vote: 참여 순위, like: 좋아요 순위
  const rankingPeriodType = rankingType === 'vote' ? 'WEEKLY_VOTE' : 'WEEKLY_LIKE';

  const navigate = useNavigate();
  const observeRef = useRef<HTMLDivElement | null>(null);
  const PAGE_SIZE = 5;
  /**
   * APIs - 통합 API 사용 (filterType: 'all', popular 정렬은 BE에서 처리)
   */
  const { data, fetchNextPage, hasNextPage, isFetching, isFetchingNextPage } = useInfiniteQuery<ISlice<QuestionV2>>({
    queryKey: ['questionList', 'popular', selectedCategory],
    queryFn: ({ pageParam = 0 }) => {
      const params = {
        page: pageParam as number,
        size: PAGE_SIZE,
        filterType: 'popular' as const,
        categoryId: selectedCategory === 'all' ? undefined : selectedCategory,
      };
      return questionApi.getUnifiedQuestionList(params);
    },
    getNextPageParam: (lastPage, allPages) => {
      return lastPage.hasNext ? allPages.length : undefined;
    },
    initialPageParam: 0,
  });

  /** S6-27: 실시간 랭킹 조회 (참여/좋아요 분리) */
  const { data: leaderboard } = useQuery<LeaderboardRankingResponse>({
    queryKey: ['leaderboard', rankingPeriodType],
    queryFn: () => userRewardApi.getLiveLeaderboard(rankingPeriodType).then((res) => res.data.results),
    enabled: activeTab === 'ranking',
    staleTime: 0,
    gcTime: 0,
  });

  /** 보상 기준 조회 */
  const { data: tiers } = useQuery<LeaderboardTierItem[]>({
    queryKey: ['leaderboardTiers', rankingPeriodType],
    queryFn: () => userRewardApi.getLeaderboardTiers(rankingPeriodType).then((res) => res.data.results ?? res.data),
    enabled: activeTab === 'ranking',
    staleTime: 0,
    gcTime: 0,
  });

  useEffect(() => {
    if (!observeRef.current) return;
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && hasNextPage && !isFetchingNextPage) {
          fetchNextPage();
        }
      },
      { threshold: 1.0 },
    );
    observer.observe(observeRef.current);
    return () => observer.disconnect();
  }, [hasNextPage, isFetchingNextPage, fetchNextPage]);
  const questionList = data?.pages.flatMap((page) => page.content) ?? [];
  const isInitialLoading = isFetching && !isFetchingNextPage;

  /** 인기설문 피드 광고 조회 */
  const { data: adsData } = useQuery({
    queryKey: ['ads', 'POPULAR_FEED'],
    queryFn: () => adApi.getAdsByPosition('POPULAR_FEED').then((res) => res.data),
    staleTime: 1000 * 60 * 5,
  });
  const feedAds = useMemo(() => {
    if (!adsData || adsData.length === 0) return [];
    return weightedShuffle(adsData);
  }, [adsData]);
  /**
   * Handlers
   */
  /* 카테고리 필터 옵션 선택 handler */
  const handleCategoryChange = (category: string) => {
    setSelectedCategory(category);
  };
  return (
    <main className="flex-1 max-w-6xl mx-auto px-1 sm:px-2 w-full">
      {/* S6-27: 인기설문 / 랭킹 탭 */}
      <div className="flex border-b border-gray-200 mb-4">
        <button
          type="button"
          className={`flex-1 py-3 text-sm font-medium text-center transition-colors ${
            activeTab === 'popular' ? 'text-blue-600 border-b-2 border-blue-600' : 'text-gray-500 hover:text-gray-700'
          }`}
          onClick={() => setActiveTab('popular')}
        >
          인기설문
        </button>
        <button
          type="button"
          className={`flex-1 py-3 text-sm font-medium text-center transition-colors ${
            activeTab === 'ranking' ? 'text-blue-600 border-b-2 border-blue-600' : 'text-gray-500 hover:text-gray-700'
          }`}
          onClick={() => setActiveTab('ranking')}
        >
          랭킹
        </button>
      </div>

      {activeTab === 'popular' ? (
        <>
          {/* 설문 카테고리 부분 */}
          <div>
            <CategoryFilter selectedCategory={selectedCategory} handleCategoryChange={handleCategoryChange} />
          </div>
          {/* 설문 목록 부분 - 설문 리스트 & 로딩 */}
          <div>
            <SectionLoading
              isLoading={isInitialLoading}
              skeleton={<GridSkeleton count={6} columns={{ default: 1, md: 2, lg: 3 }} variant="card" />}
            >
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {questionList.map((question: QuestionV2, index: number) => (
                  <div key={question.questionId}>
                    <QuestionCardV2 question={question} />
                    {/* 설문 4개마다 광고 삽입 */}
                    {(index + 1) % 4 === 0 && feedAds.length > 0 && (
                      <div className="mt-4">
                        <AdCard ad={feedAds[Math.floor(index / 4) % feedAds.length]} position="POPULAR_FEED" />
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </SectionLoading>
            <div
              ref={observeRef}
              style={{ height: '50px', display: 'flex', justifyContent: 'center', alignItems: 'center' }}
            >
              {isFetchingNextPage && <p>Loading more...</p>}
            </div>
          </div>
        </>
      ) : (
        /* S6-27: 랭킹 탭 (설문 단위 · 참여/좋아요 분리) */
        <div className="px-2 pt-2">
          {/* 참여 순위 / 좋아요 순위 서브탭 */}
          <div className="flex bg-gray-100 rounded-xl p-1 mb-4">
            {(['vote', 'like'] as const).map((type) => (
              <button
                key={type}
                type="button"
                className={`flex-1 py-2 text-sm font-medium rounded-lg transition-colors ${
                  rankingType === type ? 'bg-white text-blue-600 shadow-sm' : 'text-gray-500 hover:text-gray-700'
                }`}
                onClick={() => setRankingType(type)}
              >
                {type === 'vote' ? '참여 순위' : '좋아요 순위'}
              </button>
            ))}
          </div>

          {/* 집계 기간 + 남은 일수 */}
          {leaderboard?.periodEnd && (
            <div className="flex flex-col items-center mb-4">
              <p className="text-xs text-gray-500">
                {leaderboard.periodStart} ~ {leaderboard.periodEnd}
              </p>
              <p className="text-xs font-bold text-red-500 mt-0.5">
                {(() => {
                  const end = new Date(leaderboard.periodEnd + 'T23:59:59');
                  const now = new Date();
                  const diff = Math.ceil((end.getTime() - now.getTime()) / (1000 * 60 * 60 * 24));
                  return diff > 0 ? `${diff}일 남았어요` : '집계 중이에요';
                })()}
              </p>
            </div>
          )}

          {leaderboard?.rankings && leaderboard.rankings.length > 0 ? (
            <div className="divide-y divide-gray-100">
              {leaderboard.rankings.map((item) => (
                <button
                  key={item.questionId}
                  type="button"
                  className="w-full flex items-center justify-between py-4 text-left"
                  onClick={() => navigate(`/question/info/${item.questionId}`)}
                >
                  <div className="flex items-center gap-3 min-w-0">
                    <span className="text-lg w-8 text-center shrink-0">
                      {RANK_MEDALS[item.rank] || `${item.rank}위`}
                    </span>
                    <div className="min-w-0">
                      <p className={`text-sm ${item.rank <= 3 ? 'font-bold' : 'font-medium'} text-gray-900 truncate`}>
                        {item.questionTitle}
                      </p>
                      <p className="text-xs text-gray-500 flex items-center gap-1 mt-0.5">
                        {item.categoryIcon && <img src={item.categoryIcon} alt="" width={12} height={12} />}
                        <span className="truncate">
                          {item.categoryName} · {item.nickname}
                        </span>
                      </p>
                    </div>
                  </div>
                  <span className="text-sm font-bold text-blue-600 shrink-0 ml-2">
                    {rankingType === 'vote' ? `${item.totalVotes}명` : `${item.totalLikes}개`}
                  </span>
                </button>
              ))}
            </div>
          ) : (
            <div className="py-12 text-center">
              <p className="text-sm text-gray-500">아직 이번 주 랭킹 데이터가 없어요</p>
            </div>
          )}

          {/* 보상 기준 */}
          {tiers && tiers.length > 0 && (
            <div className="mt-6 p-4 bg-gray-50 rounded-xl">
              <p className="text-xs font-bold text-gray-700 mb-2">
                {rankingType === 'vote' ? '참여 순위' : '좋아요 순위'} 보상
              </p>
              {tiers.map((tier, i) => (
                <div key={tier.id ?? i} className="flex justify-between py-1">
                  <span className="text-xs text-gray-600">
                    {tier.rankFrom === tier.rankTo ? `${tier.rankFrom}위` : `${tier.rankFrom}~${tier.rankTo}위`}
                  </span>
                  <span className="text-xs font-bold text-blue-600">{tier.rewardAmount}조각</span>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </main>
  );
}
